"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

/**
 * Neuric chat widget (Ungdomskraft AB workspace). Loaded once from the root
 * layout; the loader script sets up the window.Neuric command queue and pulls
 * the versioned bundle. The workspace ID is public — access is controlled by
 * the allowed-websites list in Neuric Desk (Settings → Widget → Installation).
 *
 * Widget language follows the site locale (/en → "en", otherwise "sv") instead
 * of the browser language, so a Swedish visitor on the English site gets the
 * English widget and vice versa.
 *
 * On the booking page the widget is told so (`context`), together with a
 * service picked through ?service=: the assistant then books in the chat
 * (Neuric procedure "Boka i chatten") instead of pointing back to the form.
 */
const WORKSPACE_ID = "0dcee15a-e7a4-4c1a-8848-1b8901beb3b7";
const API_URL = "https://neuric-production.up.railway.app";
const LOADER_URL = "https://neuric-widget-production.up.railway.app/widget.js";

type NeuricWindow = Window & {
  Neuric?: ((command: string, argument?: unknown) => void) & { q?: unknown[] };
  neuricSettings?: Record<string, unknown>;
};

function localeFor(pathname: string): "sv" | "en" {
  return pathname === "/en" || pathname.startsWith("/en/") ? "en" : "sv";
}

function contextFor(pathname: string): Record<string, string> {
  if (pathname !== "/boka" && pathname !== "/en/book") return {};
  const service = new URLSearchParams(window.location.search).get("service");
  return service ? { page: "boka", service } : { page: "boka" };
}

export default function NeuricWidget() {
  const pathname = usePathname();
  const language = localeFor(pathname ?? "/");
  const loaded = useRef(false);

  useEffect(() => {
    const w = window as NeuricWindow;
    const context = contextFor(pathname ?? "/");
    if (loaded.current) {
      w.Neuric?.("update", { language, context });
      return;
    }
    loaded.current = true;

    w.neuricSettings = { workspaceId: WORKSPACE_ID, apiUrl: API_URL, language };
    if (typeof w.Neuric !== "function") {
      const queue = ((...args: unknown[]) => {
        (queue.q ||= []).push(args);
      }) as NonNullable<NeuricWindow["Neuric"]>;
      queue.q = [];
      w.Neuric = queue;
    }

    if (document.querySelector(`script[src="${LOADER_URL}"]`)) return;
    const script = document.createElement("script");
    script.src = LOADER_URL;
    script.async = true;
    document.body.appendChild(script);
    w.Neuric?.("update", { context });
  }, [language, pathname]);

  return null;
}
